import { useState } from "react";

import { Button, Group, Modal, Radio, Stack, Text } from "@mantine/core";
import { IconDownload } from "@tabler/icons-react";
import { useRouter } from "next/router";

import { useTrackerUser } from "components/AuthProvider";

type ExportModalProps = {
  opened: boolean;
  onClose: () => void;
  currentQueryString: string;
};

export function ExportModal({ opened, onClose, currentQueryString }: ExportModalProps) {
  const [exportType, setExportType] = useState<"summary" | "complete">("summary");
  const router = useRouter();
  const user = useTrackerUser();

  const handleExport = () => {
    const queryParams = new URLSearchParams({
      query: currentQueryString,
      exportType: exportType,
    });

    const pdfUrl = `/api/benchmarking/pdf?${queryParams.toString()}`;
    window.open(pdfUrl, "_blank");
    onClose();
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Export data" centered size="md">
      <Stack gap="md">
        <Text>Choose your export format:</Text>

        <Radio.Group
          value={exportType}
          onChange={(value) => setExportType(value as "summary" | "complete")}
        >
          <Stack mt="xs">
            <Radio
              value="summary"
              label="Data points on screen"
              description="Export just the data points on screen"
            />
            <Radio
              value="complete"
              label="All data points"
              description="Export all data points (the list may be long)"
            />
          </Stack>
        </Radio.Group>

        <Group justify="flex-end" mt="lg">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            leftSection={<IconDownload size={16} />}
            variant="filled"
            color="brand"
            onClick={handleExport}
          >
            Export
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// Define separate props for UpgradeModal
type UpgradeModalProps = {
  opened: boolean;
  onClose: () => void;
};

// Update UpgradeModal to use its own props type
export function UpgradeModal({ opened, onClose }: UpgradeModalProps) {
  return (
    <Modal opened={opened} onClose={onClose} title="Upgrade required" centered size="md">
      <Stack gap="md" py="md">
        <Text>
          Export functionality is available exclusively to Pro users. Upgrade your account to access
          comprehensive data exports and other premium features.
        </Text>

        <Group justify="flex-end" mt="lg">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button component="a" href="/pro" variant="filled" color="brand">
            Join the waitlist
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
